import { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";

const mainNav = [
  { label: "首頁", href: "/" },
  { label: "關於保險門神", href: "/about" },
  { label: "服務項目", href: "/services", hasSubmenu: true },
  { label: "客戶見證", href: "/testimonials" },
  { label: "市場趨勢動態", href: "/insights" },
  { label: "常見問答", href: "/faq" },
  { label: "聯絡我們", href: "/contact" },
];

const serviceSubmenu = [
  { label: "保單門神（健診｜整合）", href: "/services/policy" },
  { label: "財產門神（資產配置｜風險控管）", href: "/services/asset" },
  { label: "人生門神（教育金｜退休｜傳承）", href: "/services/life" },
  { label: "企業門神（守護企業主的事業）", href: "/services/enterprise" },
];

export default function Header() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const mobileMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
    setMobileServicesOpen(false);
  }, [location.pathname]);

  const isActive = (href: string) => location.pathname === href;
  const isServiceActive = location.pathname.startsWith("/services/");

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-[999] transition-all duration-300 ${
        scrolled
          ? "bg-[#0B0B0B]/95 backdrop-blur-md border-b border-brand-border"
          : "bg-transparent"
      }`}
    >
      <div className="container-main flex items-center justify-between h-16 md:h-20">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2 shrink-0">
          <img
            src="https://static.readdy.ai/image/c58d2dc5fdc4f4caa90264537c733184/0968609e615b455b3e5560365ff74ad7.png"
            alt="保險門神 Insurance Guardian"
            className="h-8 w-8 object-contain"
          />
          <span className="text-white font-display font-semibold text-[1.0625rem] tracking-wide whitespace-nowrap">
            保險門神
          </span>
          <span className="hidden sm:inline text-brand-text-secondary text-[0.8125rem] font-light tracking-wider">
            Insurance Guardian
          </span>
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden lg:flex items-center gap-1">
          {mainNav.map((link) => (
            <div key={link.href} className="relative group">
              {link.hasSubmenu ? (
                <>
                  <Link
                    to={link.href}
                    className={`inline-flex items-center gap-1 px-3 py-2 text-[1.0625rem] font-medium transition-colors whitespace-nowrap rounded-md ${
                      isActive(link.href) || isServiceActive
                        ? "text-brand-gold"
                        : "text-brand-text-secondary hover:text-white"
                    }`}
                  >
                    {link.label}
                    <i className="ri-arrow-down-s-line text-[0.875rem]" />
                  </Link>
                  {/* Desktop Dropdown */}
                  <div className="absolute top-full left-0 pt-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-[1000]">
                    <div className="bg-[#111216] border border-[rgba(212,175,55,0.28)] rounded-xl shadow-2xl shadow-black/50 min-w-[300px] py-2">
                      {serviceSubmenu.map((sub) => (
                        <Link
                          key={sub.href}
                          to={sub.href}
                          className={`block px-5 py-3 text-[1rem] transition-colors whitespace-nowrap ${
                            isActive(sub.href)
                              ? "text-brand-gold bg-brand-gold/10"
                              : "text-brand-text-secondary hover:text-brand-gold hover:bg-white/5"
                          }`}
                        >
                          {sub.label}
                        </Link>
                      ))}
                    </div>
                  </div>
                </>
              ) : (
                <Link
                  to={link.href}
                  className={`px-3 py-2 text-[1.0625rem] font-medium transition-colors whitespace-nowrap rounded-md ${
                    isActive(link.href)
                      ? "text-brand-gold"
                      : "text-brand-text-secondary hover:text-white"
                  }`}
                >
                  {link.label}
                </Link>
              )}
            </div>
          ))}
        </nav>

        {/* Desktop CTA */}
        <a
          href="https://lin.ee/o51JBHX"
          target="_blank"
          rel="noopener noreferrer"
          className="hidden lg:inline-flex items-center gap-2 bg-brand-gold text-brand-black px-5 py-2.5 rounded-lg text-[1rem] font-semibold hover:bg-brand-gold-soft transition-colors whitespace-nowrap"
        >
          <i className="ri-line-fill" />
          加入 LINE 諮詢
        </a>

        {/* Hamburger */}
        <button
          onClick={() => setMenuOpen(!menuOpen)}
          className="lg:hidden flex flex-col justify-center items-center w-10 h-10 gap-1.5 relative z-[1000]"
          aria-label="選單"
        >
          <span
            className={`block w-6 h-0.5 bg-brand-gold transition-transform duration-300 ${
              menuOpen ? "rotate-45 translate-y-2" : ""
            }`}
          />
          <span
            className={`block w-6 h-0.5 bg-brand-gold transition-opacity duration-300 ${
              menuOpen ? "opacity-0" : ""
            }`}
          />
          <span
            className={`block w-6 h-0.5 bg-brand-gold transition-transform duration-300 ${
              menuOpen ? "-rotate-45 -translate-y-2" : ""
            }`}
          />
        </button>
      </div>

      {/* Mobile Menu */}
      <div
        ref={mobileMenuRef}
        className={`lg:hidden fixed inset-0 top-16 bg-[#0B0B0B]/98 backdrop-blur-xl transition-all duration-300 overflow-y-auto ${
          menuOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
      >
        <nav className="flex flex-col items-stretch px-6 py-8 gap-0">
          {mainNav.map((link) => (
            <div key={link.href}>
              {link.hasSubmenu ? (
                <div>
                  <button
                    onClick={() => setMobileServicesOpen(!mobileServicesOpen)}
                    className={`flex items-center justify-between w-full px-2 py-3 text-[1.125rem] font-medium transition-colors border-b border-white/10 ${
                      isServiceActive
                        ? "text-brand-gold"
                        : "text-white hover:text-brand-gold"
                    }`}
                  >
                    <span>{link.label}</span>
                    <i
                      className={`ri-arrow-down-s-line text-[1.25rem] transition-transform duration-200 ${
                        mobileServicesOpen ? "rotate-180" : ""
                      }`}
                    />
                  </button>
                  <div
                    className={`overflow-hidden transition-all duration-300 ${
                      mobileServicesOpen
                        ? "max-h-[500px] opacity-100"
                        : "max-h-0 opacity-0"
                    }`}
                  >
                    <div className="pl-4 py-1">
                      {serviceSubmenu.map((sub) => (
                        <Link
                          key={sub.href}
                          to={sub.href}
                          className={`block px-4 py-3 text-[1rem] transition-colors border-b border-white/5 ${
                            isActive(sub.href)
                              ? "text-brand-gold"
                              : "text-brand-text-secondary hover:text-brand-gold"
                          }`}
                        >
                          {sub.label}
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <Link
                  to={link.href}
                  className={`block px-2 py-3 text-[1.125rem] font-medium transition-colors border-b border-white/10 ${
                    isActive(link.href)
                      ? "text-brand-gold"
                      : "text-white hover:text-brand-gold"
                  }`}
                >
                  {link.label}
                </Link>
              )}
            </div>
          ))}
          <a
            href="https://lin.ee/o51JBHX"
            target="_blank"
            rel="noopener noreferrer"
            className="mt-6 flex items-center justify-center gap-2 bg-brand-gold text-brand-black px-8 py-3 rounded-lg text-[1.125rem] font-semibold"
          >
            <i className="ri-line-fill" />
            加入 LINE 諮詢
          </a>
        </nav>
      </div>
    </header>
  );
}